/* src/api/axios.js */
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/useNotificationStore'
import router from '@/router'

// Create an Axios instance with default headers
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-hash-key': import.meta.env.VITE_X_HASH_KEY,
    'x-app-key': import.meta.env.VITE_X_APP_KEY,
  },
})

// Helper function to get acquisition source
function getAcquisitionSource() {
  // Check for UTM parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const utmSource = urlParams.get('utm_source')
  if (utmSource) {
    localStorage.setItem('referrer', utmSource)
    return utmSource
  }

  // Fall back to stored referrer
  const storedReferrer = localStorage.getItem('referrer')
  if (storedReferrer) {
    return storedReferrer
  }

  // Default to empty string
  return ''
}

// ─── 1) Request interceptor ───────────────────────────────────────────────
//    - Sets x-ua-key based on UTM params > stored referrer > ''
//    - Adds x-access-key if logged in
apiClient.interceptors.request.use(
  config => {
    config.headers['x-ua-key'] = getAcquisitionSource()

    const { accessToken } = useAuthStore()
    if (accessToken) {
      config.headers['x-access-key'] = accessToken
    } else {
      delete config.headers['x-access-key']
    }
    return config
  },
  err => Promise.reject(err)
)

// ─── 2) HTTP-level 401 (direct from server) ─────────────────────────────────
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const auth = useAuthStore()
      const notify = useNotificationStore()
      auth.logout()
      notify.showNotification('Session expired. Please log in again.', 'error')
      router.push({ name: 'login' })
    }
    return Promise.reject(error)
  }
)

// ─── 3) HTTP-level 500 (server error) ───────────────────────────────────────
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 500) {
      const notify = useNotificationStore()
      notify.showNotification('Server error. Please try again later.', 'error')
    }
    return Promise.reject(error)
  }
)

// ─── 4) Business-level 401 (nested envelope) ────────────────────────────────
apiClient.interceptors.response.use(response => {
  const payload = response.data
  if (payload.code === 'Error' && payload.data?.code === 401) {
    const apiMsg =
      payload.data.message ||
      payload.statusDescription ||
      'Session expired. Please log in again.'

    const auth = useAuthStore()
    const notify = useNotificationStore()
    auth.logout()
    notify.showNotification(apiMsg, 'error')
    router.push({ name: 'login' })
    return Promise.reject(new Error(apiMsg))
  }
  return response
})

// ─── 5) Centralize "425 → VerifyAccount" logic ─────────────────────────────
apiClient.interceptors.response.use(response => {
  const inner = response.data
  if (inner.code === 425 && inner.data) {
    const auth = useAuthStore()
    const notify = useNotificationStore()
    const body = JSON.parse(response.config.data || '{}')
    auth.phone = body.user_id
    auth.acc_no = inner.data.acc_no
    notify.showNotification(inner.message, 'warning')
    router.push({
      name: 'verify-account',
      query: { msisdn: auth.phone, fromLogin: true },
    })
    return Promise.reject(new Error('Needs verification'))
  }
  return response
})

export default apiClient
